import axios from "axios";
import apiClient, { ENDPOINTS } from "@/core/api/apiClient";
import { SubscribeFormData, SubscribeFormResponse } from "./types";

// Cache for duplicate submission prevention
const submissionCache = new Map<string, number>();
const CACHE_DURATION = 5000; // 5 seconds

/**
 * Prevents duplicate submissions within a short time frame
 */
const isDuplicateSubmission = (email: string): boolean => {
  const now = Date.now();
  const lastSubmission = submissionCache.get(email);

  if (lastSubmission && now - lastSubmission < CACHE_DURATION) {
    return true;
  }

  submissionCache.set(email, now);

  // Clean up old entries
  for (const [cachedEmail, timestamp] of submissionCache.entries()) {
    if (now - timestamp > CACHE_DURATION) {
      submissionCache.delete(cachedEmail);
    }
  }

  return false;
};

/**
 * Validates email format with enhanced regex
 */
const validateEmail = (email: string): boolean => {
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(email) && email.length <= 254;
};

/**
 * Submit the newsletter subscription form with enhanced error handling
 */
export const submitSubscribeForm = async (
  formData: SubscribeFormData
): Promise<SubscribeFormResponse> => {
  // Input validation
  if (!formData.email?.trim()) {
    return {
      success: false,
      message: "Email address is required.",
    };
  }

  const email = formData.email.trim().toLowerCase();

  if (!validateEmail(email)) {
    return {
      success: false,
      message: "Please enter a valid email address.",
    };
  }

  // Check for duplicate submissions
  if (isDuplicateSubmission(email)) {
    return {
      success: false,
      message: "Please wait before submitting again.",
    };
  }

  try {
    // Prepare the payload
    const payload = {
      email,
      source: formData.source || "homepage_cta",
      timestamp: Date.now(),
    };

    // Create abort controller for request timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    try {
      const response = await apiClient.post(
        ENDPOINTS.NEWSLETTER_SIGN_UP,
        payload,
        {
          signal: controller.signal,
          timeout: 10000,
        }
      );

      clearTimeout(timeoutId);

      if (response.status >= 200 && response.status < 300) {
        return {
          success: true,
          message: "Thank you for subscribing to our newsletter!",
        };
      } else {
        return {
          success: false,
          message:
            "There was an error processing your subscription. Please try again.",
        };
      }
    } catch (requestError) {
      clearTimeout(timeoutId);
      throw requestError;
    }
  } catch (error) {
    // Enhanced error handling
    if (axios.isAxiosError(error)) {
      if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
        return {
          success: false,
          message:
            "Request timed out. Please check your connection and try again.",
        };
      }

      if (error.response) {
        const status = error.response.status;

        switch (status) {
          case 409:
            return {
              success: true, // UX: treat as success
              message: "You're already subscribed to our newsletter!",
            };
          case 400:
            return {
              success: false,
              message: "Please enter a valid email address.",
            };
          case 429:
            return {
              success: false,
              message: "Too many requests. Please try again in a few minutes.",
            };
          case 500:
          case 502:
          case 503:
          case 504:
            return {
              success: false,
              message:
                "Our server is temporarily unavailable. Please try again later.",
            };
          default:
            return {
              success: false,
              message:
                "There was an error processing your subscription. Please try again.",
            };
        }
      } else if (error.request) {
        return {
          success: false,
          message:
            "Unable to connect to our server. Please check your internet connection.",
        };
      }
    }

    // Generic error fallback
    return {
      success: false,
      message: "An unexpected error occurred. Please try again later.",
    };
  }
};

/**
 * Clear submission cache (useful for testing or manual cleanup)
 */
export const clearSubmissionCache = (): void => {
  submissionCache.clear();
};
